import React, { useEffect, useRef, useState } from 'react';

import { Animated, Image, ImageProps, ImageStyle, Platform, StyleProp, StyleSheet, View } from 'react-native';
import { MB_Pressable, MB_PressableStateCallbackType } from '@/components/general/pressable';
import { MB_Modal } from '@/components/general/modal';
import { mbPlatformStyle } from '@/utils/general/style-utils';
import { MB_ImageProps, brokenImageComponent } from './MB_ImageUtils';

const MB_Image = (props: MB_ImageProps) => {
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const hoverScaleAnimation = useRef(new Animated.Value(0)).current;
  const [errorLoading, setErrorLoading] = useState(false);

  const onImagePressed = () => {
    props.onPress?.();
    if (props.allowModalPresentationOnPress) {
      setIsImageModalVisible(true);
    }
  };
  const onLongPressed = () => {
    props.onLongPress?.();
    if (props.allowModalPresentationOnPress) {
      setIsImageModalVisible(true);
    }
  };

  const imageProps: ImageProps = React.useMemo(() => {
    const source = typeof props.source === 'string' ? { uri: props.source } : props.source;
    let defaultSource = Array.isArray(source) ? undefined : source;

    const webProps: ImageProps = {
      resizeMode: props.resizeMode,
      resizeMethod: props.resizeMethod,
      source,
      defaultSource,
    };

    return webProps;
  }, [props.resizeMethod, props.resizeMode, props.source]);

  useEffect(() => {
    setErrorLoading(false);
  }, [props.source]);

  const animateImage = (toValue: number) => {
    if (!props.disableOnHoverAnimations) {
      Animated.timing(hoverScaleAnimation, {
        toValue,
        duration: 500,
        useNativeDriver: false,
      }).start();
    }
  };

  const grayscaleStyle = (!props.grayscaleFilter || Platform.OS !== 'web' ? {} : { filter: `grayscale(${props.grayscaleFilter})` }) as StyleProp<ImageStyle>;

  function onError() {
    if (!props.disableBrokenImage) {
      setErrorLoading(true);
    }
  }

  return (
    <View style={[styles.container, props.style]}>
      <MB_Modal isVisible={isImageModalVisible} onDismiss={setIsImageModalVisible} childrenWrapperStyle={styles.modalWrapper}>
        {errorLoading ? <>{brokenImageComponent(props.brokenImageComponent)}</> : <Image resizeMode="contain" {...imageProps} style={styles.modalImage} onError={onError} />}
      </MB_Modal>

      <MB_Pressable disabled={props.disableInteraction} onPress={onImagePressed} onLongPress={onLongPressed} style={[styles.pressableWrapper]}>
        {(state: MB_PressableStateCallbackType) => {
          props.onHoverChange?.(state);

          animateImage(state.hovered || props.activateHover ? 1 : 0);

          if (errorLoading) {
            return brokenImageComponent(props.brokenImageComponent);
          }

          return (
            <Animated.Image
              {...imageProps}
              style={[
                styles.imageStyle,
                grayscaleStyle,
                {
                  transform: [{ scale: hoverScaleAnimation.interpolate({ inputRange: [0, 1], outputRange: [1, 1.1] }) }],
                },
              ]}
              onError={onError}
            />
          );
        }}
      </MB_Pressable>
    </View>
  );
};

export { MB_Image };

const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
  },
  pressableWrapper: {
    width: '100%',
    height: '100%',
  },
  imageStyle: {
    width: '100%',
    height: '100%',
  },
  modalWrapper: {
    width: '100%',
    overflow: 'hidden',
    ...mbPlatformStyle({
      web: {
        height: '100%',
      },
      mobile: {
        aspectRatio: 1,
      },
    }),
  },
  modalImage: {
    width: '100%',
    height: '100%',
  },
});
